import React, { useState, useEffect } from 'react';
import {
  Container,
  Button,
  Row,
  Col,
  Form,
  Alert,
  Spinner,
  Card,
  Badge
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

// Helper function to format numbers
const formatNumber = (num) => {
  return new Intl.NumberFormat('en-US').format(num || 0);
};

const BillingReport = () => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState([]);
  const [reportStats, setReportStats] = useState(null);

  useEffect(() => {
    fetchClients();
    // Set default dates to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay);
    setEndDate(lastDay);
  }, []);

  const fetchClients = async () => {
    try {
      const API_TOKEN = 'Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq';
      const response = await fetch(
        `https://app.yyzprep.ca/api/merchants?api_token=${API_TOKEN}`
      );
      const data = await response.json();
      setClients(data.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Failed to load clients');
    }
  };

  const generateReport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });

      if (selectedClient) {
        params.append('clientId', selectedClient);
      }

      const response = await fetch(`${API_BASE_URL}/api/billing-report?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const data = await response.json();
      setReportData(data);
      
      // Calculate statistics
      if (data && data.length > 0) {
        const stats = {
          totalItems: data.length,
          uniqueASINs: new Set(data.map(item => item.asin || item.item_name)).size,
          uniqueOutbounds: new Set(data.map(item => item.outbound_id)).size,
          serviceBreakdown: data.reduce((acc, item) => {
            acc[item.service_type] = (acc[item.service_type] || 0) + 1;
            return acc;
          }, {}),
          totalUnits: data.reduce((acc, item) => {
            if (item.service_type === 'Unit Labels') {
              return acc + (item.quantity || 0);
            }
            return acc;
          }, 0)
        };
        setReportStats(stats);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setError('Failed to generate report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (reportData.length === 0) {
      setError('No data to download');
      return;
    }

    const headers = [
      'Outbound ID',
      'Outbound Name', 
      'Client Name',
      'Status',
      'Date Finished',
      'Item Name',
      'Quantity',
      'Service Type',
      'Service Charge'
    ];

    const csvContent = [
      headers.join(','),
      ...reportData.map(row => [
        row.outbound_id,
        `"${row.outbound_name || ''}"`,
        `"${row.client_name || ''}"`,
        row.status || '',
        row.date_finished || '',
        `"${row.item_name || ''}"`,
        row.quantity || 0,
        `"${row.service_type || ''}"`,
        formatCurrency(parseFloat(row.service_charge || '0'))
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      const clientName = selectedClient ? 
        clients.find(c => c.id.toString() === selectedClient)?.name || 'Selected' : 
        'All';
      
      const filename = `billing-report-${clientName}-${startDate.toISOString().split('T')[0]}-to-${endDate.toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const downloadExcel = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });

      if (selectedClient) {
        params.append('clientId', selectedClient);
      }

      const response = await fetch(`${API_BASE_URL}/api/billing-report-excel?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to generate Excel report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const clientName = selectedClient ? 
        clients.find(c => c.id.toString() === selectedClient)?.name || 'Selected' : 
        'All';
      
      const filename = `billing-report-${clientName}-${startDate.toISOString().split('T')[0]}-to-${endDate.toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Excel report:', error);
      setError('Failed to generate Excel report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Container>
        {/* Header */}
        <div className="text-center mb-5">
          <h2 className="display-6 fw-bold text-primary mb-2">💰 Billing Report Generator</h2>
          <p className="lead text-muted">Generate detailed billing reports with cost analysis for your outbound shipments</p>
        </div>

        {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

        {/* Configuration Card */}
        <Card className="shadow-sm mb-4">
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">📊 Report Configuration</h5>
          </Card.Header>
          <Card.Body>
            <Row className="g-4">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">📅 Start Date</Form.Label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    className="form-control"
                    dateFormat="MMM dd, yyyy"
                    placeholderText="Select start date"
                  />
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">📅 End Date</Form.Label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    className="form-control"
                    dateFormat="MMM dd, yyyy"
                    placeholderText="Select end date"
                  />
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">🏢 Client (Optional)</Form.Label>
                  <Form.Select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    className="form-control"
                  >
                    <option value="">All Clients</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <div className="text-center mt-4">
              <Button 
                size="lg"
                variant="primary" 
                onClick={generateReport}
                disabled={loading}
                className="px-5"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Generating Report...
                  </>
                ) : (
                  <>🔍 Generate Report</>
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Statistics Cards */}
        {reportStats && (
          <Row className="mb-4">
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{backgroundColor: '#e3f2fd'}}>
                <Card.Body className="text-center">
                  <div className="display-6 text-primary">📦</div>
                  <h3 className="text-primary mb-1">{formatNumber(reportStats.totalUnits)}</h3>
                  <p className="text-muted mb-0">Total Units</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{backgroundColor: '#f3e5f5'}}>
                <Card.Body className="text-center">
                  <div className="display-6 text-purple">🎯</div>
                  <h3 className="text-purple mb-1">{formatNumber(reportStats.uniqueASINs)}</h3>
                  <p className="text-muted mb-0">Unique ASINs</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{backgroundColor: '#e8f5e8'}}>
                <Card.Body className="text-center">
                  <div className="display-6 text-success">📋</div>
                  <h3 className="text-success mb-1">{formatNumber(reportStats.uniqueOutbounds)}</h3>
                  <p className="text-muted mb-0">Outbound Shipments</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{backgroundColor: '#fff3e0'}}>
                <Card.Body className="text-center">
                  <div className="display-6 text-warning">📝</div>
                  <h3 className="text-warning mb-1">{formatNumber(reportStats.totalItems)}</h3>
                  <p className="text-muted mb-0">Service Line Items</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Service Breakdown */}
        {reportStats && (
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-info text-white">
              <h6 className="mb-0">🔧 Service Breakdown</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                {Object.entries(reportStats.serviceBreakdown).map(([service, count]) => (
                  <Col md={4} key={service} className="mb-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">{service}</span>
                      <Badge bg="secondary">{formatNumber(count)} items</Badge>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Download Section */}
        {reportData.length > 0 && (
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">💾 Download Reports</h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <p className="lead mb-4">
                  Report generated successfully! Found <strong>{formatNumber(reportData.length)}</strong> service line items 
                  across <strong>{reportStats?.uniqueOutbounds}</strong> outbound shipments.
                </p>
                
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <Button 
                    size="lg"
                    variant="outline-success" 
                    onClick={downloadCSV}
                    className="px-4"
                  >
                    📄 Download CSV ({formatNumber(reportData.length)} rows)
                  </Button>
                  
                  <Button 
                    size="lg"
                    variant="success" 
                    onClick={downloadExcel}
                    disabled={loading}
                    className="px-4"
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Generating Excel...
                      </>
                    ) : (
                      <>📊 Download Excel (with Cost Calculator)</>
                    )}
                  </Button>
                </div>
                
                <Alert variant="info" className="mt-4 text-start">
                  <Alert.Heading className="h6">📊 Excel Features:</Alert.Heading>
                  <ul className="mb-0">
                    <li><strong>Service Rates Sheet:</strong> Enter your costs per service type</li>
                    <li><strong>Details Sheet:</strong> All transaction data with automatic cost calculations</li>
                    <li><strong>Summary by ASIN:</strong> Average cost per unit for each product</li>
                  </ul>
                </Alert>
              </div>
            </Card.Body>
          </Card>
        )}
      </Container>
    </Container>
  );
};

export default BillingReport;